import { useState, ReactNode } from 'react';
import { Card, CardContent } from '../ui/card';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface HelpPanelProps {
  /** Unique key per page; dismissal is remembered as admin-help-<storageKey> */
  storageKey: string;
  children: ReactNode;
}

/**
 * Collapsible "How this page works" explainer for admin pages.
 * Starts expanded on first visit; collapsed state persists in localStorage.
 */
export function HelpPanel({ storageKey, children }: HelpPanelProps) {
  const lsKey = `admin-help-${storageKey}`;
  const [open, setOpen] = useState<boolean>(() => localStorage.getItem(lsKey) !== 'collapsed');

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(lsKey, next ? 'expanded' : 'collapsed');
  };

  return (
    <Card className="mb-6 border-blue-100 bg-blue-50/50">
      <CardContent className="pt-4 pb-4">
        <button
          type="button"
          onClick={toggle}
          className="flex items-center gap-2 w-full text-left text-sm font-medium text-blue-900"
          aria-expanded={open}
        >
          <HelpCircle className="h-4 w-4 shrink-0" />
          How this page works
          {open ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
        </button>
        {open && (
          <div className="mt-3 space-y-2 text-sm text-gray-700">
            {children}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
